import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { FilePlus } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { ExpandableMessageCard, FileName, SimpleMessageCard } from "./shared";
import { getProjectVersionIdFromResult } from "./shared/get-project-version-id";
import { useFileLinkProps } from "./useFileLinkProps";

interface InsertTextProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  messageId: string;
}

export function InsertText({ part, messageId }: InsertTextProps) {
  // Extract the inserted text from part.args
  const newStr = part.args?.new_str as string;
  const path = part.args?.path as string;
  const insertLine = part.args?.insert_line as number;
  const snapshotId = getProjectVersionIdFromResult(part);
  const projectId = useActiveProjectId();
  const linkProps = useFileLinkProps(
    projectId,
    messageId,
    snapshotId,
    path || "",
  );

  const title = getTitle(part.status || "", insertLine);
  const hasContent = !!newStr;

  const fileLink = (
    <FileName
      filename={path}
      linkProps={linkProps}
      variant={hasContent ? "outline" : "default"}
    />
  );

  // If there's no content to show, use SimpleMessageCard
  if (!hasContent) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={FilePlus}
        title={
          <>
            {title}
            {fileLink}
          </>
        }
        className="min-w-0 border rounded-md mb-0"
      />
    );
  }

  // If there's content, use ExpandableMessageCard to show the full text
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={FilePlus}
      title={title}
      additionalContent={fileLink}
      className="min-w-0 mb-0"
      accordionValue="insert-text"
    >
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">
          Inserted at line {insertLine}:
        </div>
        <pre className="text-sm bg-muted/50 p-3 rounded-md overflow-x-auto">
          <code>{newStr}</code>
        </pre>
      </div>
    </ExpandableMessageCard>
  );
}

const getTitle = (status: string, insertLine?: number) => {
  const actionText = status === "complete" ? "Added to" : "Adding to";
  const lineText = insertLine !== undefined ? ` at line ${insertLine}` : "";

  return `${actionText}${lineText}`;
};
